import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { formatDayDe, termStars } from "@vocifuchs/shared";
import { api, ApiError } from "../lib/api.js";
import { common } from "../copy/common.js";
import { errors } from "../copy/errors.js";
import { lists } from "../copy/lists.js";
import { ListProgress, isExamAhead } from "../components/ListProgress.js";
import { Banner, Button, Stars, Started } from "../components/ui.js";
import { SpeakButton } from "../components/SpeakButton.js";
import { useSession } from "../session.js";

interface ListDetail {
  id: string;
  title: string;
  lang_b: "en" | "fr";
  due_date: string | null;
  visibility: "private" | "class" | string;
  is_owner: boolean;
  /** The child's own mark (issue #99). */
  mark: "current" | "archived" | null;
  forked_from_title: string | null;
  stars: 0 | 1 | 2 | 3;
  ready: number;
  progress: number;
  items: Array<{ term_id: string; a: string; b: string; box_ab: number; box_ba: number }>;
}

/**
 * Stars for one word: both directions count, a missing one as box 0. Below the
 * first star the word shows dots instead — otherwise a word that was practised
 * today looks exactly like one nobody has opened (docs/STARS.md §8).
 */
function ItemStars({ ab, ba }: { ab: number; ba: number }) {
  const n = termStars({ ab, ba });
  if (n === 0) {
    const started = ((ab >= 1 ? 1 : 0) + (ba >= 1 ? 1 : 0)) as 0 | 1 | 2;
    return <Started n={started} label={lists.progress.startedAria(started)} />;
  }
  return <Stars n={n} label={lists.progress.starsAria(n)} />;
}

export function ListDetailScreen() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { membership } = useSession();
  const [list, setList] = useState<ListDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!id) return;
    void api<ListDetail>(`/api/lists/${id}`)
      .then(setList)
      .catch((err: unknown) => setError(err instanceof ApiError ? err.message : errors.failed));
  }, [id]);

  const share = async (visibility: "private" | "class") => {
    if (!list) return;
    setBusy(true);
    setError(null);
    try {
      const next = await api<ListDetail>(`/api/lists/${list.id}`, {
        method: "PATCH",
        body: JSON.stringify({ visibility }),
      });
      setList(next);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : errors.failed);
    } finally {
      setBusy(false);
    }
  };

  // «Aktuell» and «Archiv» (issue #99): for this child only, also on class lists.
  const setMark = async (mark: ListDetail["mark"]) => {
    if (!list) return;
    setBusy(true);
    setError(null);
    try {
      const next = await api<{ mark: ListDetail["mark"] }>(`/api/lists/${list.id}/mark`, {
        method: "PUT",
        body: JSON.stringify({ mark }),
      });
      setList({ ...list, mark: next.mark });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : errors.failed);
    } finally {
      setBusy(false);
    }
  };

  const fork = async () => {
    if (!list) return;
    setBusy(true);
    setError(null);
    try {
      const copy = await api<{ id: string }>(`/api/lists/${list.id}/fork`, { method: "POST" });
      navigate(`/lists/${copy.id}`);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : errors.failed);
    } finally {
      setBusy(false);
    }
  };

  if (error && !list) return <Banner>{error}</Banner>;
  if (!list) return <p className="text-ink-soft">{common.loading}</p>;

  const shared = list.visibility === "class";

  return (
    <div className="space-y-6 lg:space-y-8">
      <Link
        to="/"
        className="inline-flex items-center gap-1 font-semibold text-fox-ink underline-offset-4 hover:underline"
      >
        {common.backToStart}
      </Link>

      <header className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="text-3xl sm:text-4xl">{list.title}</h1>
          {list.forked_from_title ? (
            <p className="mt-2 text-ink-soft">{lists.detail.forkedFrom(list.forked_from_title)}</p>
          ) : null}
          <p className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1 text-ink-soft">
            <span>{common.lang[list.lang_b]}</span>
            <span aria-hidden>·</span>
            <span>{common.words(list.items.length)}</span>
            {list.due_date && !isExamAhead(list.due_date) ? (
              <>
                <span aria-hidden>·</span>
                <span>{common.exam(formatDayDe(list.due_date))}</span>
              </>
            ) : null}
            <span aria-hidden>·</span>
            <ListProgress
              stars={list.stars}
              ready={list.ready}
              progress={list.progress}
              total={list.items.length}
              dueDate={list.due_date}
            />
          </p>
        </div>
        <div className="flex w-full flex-col gap-2 sm:w-56 sm:shrink-0">
          <Button onClick={() => navigate(`/practice/${list.id}`)}>{lists.detail.practice}</Button>
          {list.is_owner ? (
            <Button variant="ghost" onClick={() => navigate(`/lists/${list.id}/edit`)}>
              {lists.detail.edit}
            </Button>
          ) : null}
          {list.is_owner && membership ? (
            <Button
              variant="ghost"
              disabled={busy}
              onClick={() => void share(shared ? "private" : "class")}
            >
              {shared ? lists.detail.unshare : lists.detail.share}
            </Button>
          ) : null}
          {!list.is_owner ? (
            <Button variant="ghost" disabled={busy} onClick={() => void fork()}>
              {lists.detail.copy}
            </Button>
          ) : null}
          <Button
            variant="ghost"
            disabled={busy}
            onClick={() => void setMark(list.mark === "current" ? null : "current")}
          >
            {list.mark === "current" ? lists.detail.unmarkCurrent : lists.detail.markCurrent}
          </Button>
          <Button
            variant="ghost"
            disabled={busy}
            onClick={() => void setMark(list.mark === "archived" ? null : "archived")}
          >
            {list.mark === "archived" ? lists.detail.unarchive : lists.detail.archive}
          </Button>
          <Button variant="ghost" onClick={() => navigate(`/lists/${list.id}/print`)}>
            {lists.detail.print}
          </Button>
        </div>
      </header>
      {error ? <Banner>{error}</Banner> : null}

      <ul className="grid grid-cols-1 gap-2 sm:grid-cols-2 sm:gap-3 xl:grid-cols-3">
        {list.items.map((item) => (
          <li
            key={item.term_id}
            className="flex min-h-16 items-center justify-between gap-3 rounded-2xl border border-line/60 bg-paper-3 px-4 py-3"
          >
            <span className="min-w-0">
              <span className="block truncate font-semibold">{item.a}</span>
              <span
                className="block truncate text-sm text-ink-soft"
                lang={list.lang_b === "en" ? "en-GB" : "fr-FR"}
              >
                {item.b.split("|")[0]}
              </span>
            </span>
            <span className="flex shrink-0 items-center gap-2">
              <ItemStars ab={item.box_ab} ba={item.box_ba} />
              <SpeakButton text={item.b} lang={list.lang_b} label={lists.detail.speakAria(item.b.split("|")[0] ?? item.b)} size="sm" />
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}
